import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  SESSION_EXPIRED_EVENT,
  SESSION_UPDATED_EVENT,
  expireSession,
  getTokenExpirationTime,
  isPublicPath,
} from "../../services/sessionManager";

function SessionGuard() {
  const navigate = useNavigate();
  const location = useLocation();
  const timerRef = useRef(null);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const redirectIfNeeded = () => {
      if (!isPublicPath(location.pathname)) {
        navigate("/login", { replace: true });
      }
    };

    const syncSessionTimer = () => {
      clearTimer();

      const token = localStorage.getItem("token");
      if (!token) {
        redirectIfNeeded();
        return;
      }

      const expirationTime = getTokenExpirationTime(token);
      if (expirationTime === null) {
        return;
      }

      const remainingTime = expirationTime - Date.now();
      if (remainingTime <= 0) {
        expireSession();
        redirectIfNeeded();
        return;
      }

      timerRef.current = window.setTimeout(() => {
        expireSession();
        redirectIfNeeded();
      }, remainingTime);
    };

    const handleStorageChange = (event) => {
      if (!event.key || event.key === "token") {
        syncSessionTimer();
      }
    };

    const handleSessionExpired = () => {
      clearTimer();
      redirectIfNeeded();
    };

    syncSessionTimer();
    window.addEventListener(SESSION_UPDATED_EVENT, syncSessionTimer);
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      clearTimer();
      window.removeEventListener(SESSION_UPDATED_EVENT, syncSessionTimer);
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [location.pathname, navigate]);

  return null;
}

export default SessionGuard;
